import { memo, useMemo } from 'react';
import { Tooltip, Typography } from '@mui/material';
import { useWireSearch } from 'screens/syndication/views/search/syndication/WireSearchWithContext';

interface SyndicationCategoryFieldProps {
  record: {
    categories?: (string | number)[];
  };
}

const SyndicationCategoryField = memo(({ record }: SyndicationCategoryFieldProps) => {
  const { categories = [] } = record || {};
  const { channelOptions, selectedChannel, channels } = useWireSearch();
  const channelDriver = channels.find(
    (channel) => channel.value === selectedChannel,
  )?.driver;

  // Memoize all options maps for better performance
  const optionsMap = useMemo(() => {
    const getOptions = (name: string) =>
      channelOptions?.find((option) => option.name === name)?.options || [];

    return {
      category: getOptions('category'),
      subject: getOptions('subject'),
      industry: getOptions('industry'),
      geography: getOptions('geography'),
    };
  }, [channelOptions]);

  const getCategoryLabel = (category: string | number): string | null => {
    if (channelDriver === 'prnewswire2') {
      // category: "industry/rea"
      const categoryStr = String(category);
      const group = categoryStr?.split('/')?.[0] || '';
      const subGroup = categoryStr?.split('/')?.[1] || '';

      const optionsKey = group as keyof typeof optionsMap;

      if (optionsKey in optionsMap) {
        return (
          optionsMap[optionsKey]?.find(
            (option: any) =>
              String(option.value).toLowerCase() === subGroup.toLowerCase(),
          )?.label || null
        );
      }
      return null;
    }

    if (optionsMap?.category.length === 0) {
      return String(category);
    }

    return (
      optionsMap?.category?.find(
        (option: any) =>
          String(option.value).toLowerCase() === String(category).toLowerCase(),
      )?.label || null
    );
  };

  const displayText = getCategoryLabel(categories[0]);

  const tooltipContent = categories.length > 1 && (
    <>
      {categories.map((category, index) => {
        const label = getCategoryLabel(category);
        return label ? (
          <Typography
            variant="caption"
            key={index}
            sx={{
              display: 'block',
            }}
          >
            {label}
          </Typography>
        ) : null;
      })}
    </>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
    >
      <Typography
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {displayText}
        {categories.length > 1 && (
          <span style={{ fontSize: 12 }}>({categories.length})</span>
        )}
      </Typography>
    </Tooltip>
  );
});

SyndicationCategoryField.displayName = 'SyndicationCategoryField';

export default SyndicationCategoryField;
